import { useState, useEffect, useRef } from 'react';

/**
 * Generate a robust content fingerprint for change detection.
 * Uses dual hashing (DJB2 + DJB2a XOR variant) combined with content length
 * to minimize collision probability compared to a single 32-bit hash.
 */
const contentFingerprint = (str) => {
  if (!str) return '0-0-0';
  let hash1 = 0;
  let hash2 = 5381;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1) + char;
    hash1 = hash1 & hash1;
    hash2 = ((hash2 << 5) + hash2) ^ char;
    hash2 = hash2 & hash2;
  }
  return `${hash1}-${hash2}-${str.length}`;
};

/**
 * Generic hook for managing versions of any content type
 * V1: Fixed to properly sync RHS edits to LHS by detecting content changes
 *
 * @param {Object} config Configuration object
 * @param {Function} config.extractVersions Function to extract versions from conversation
 * @param {Function} config.parseContent Function to parse content for each version
 * @param {Function} config.isRelevantMessage Function to determine if a message contains relevant content
 * @param {Object} conversation The conversation object
 * @param {boolean} isStreaming Whether content is currently streaming
 * @param {string} streamingContent The current streaming content
 */
const useVersionManagementV1 = ({
  extractVersions,
  parseContent,
  isRelevantMessage,
  conversation,
  isStreaming,
  streamingContent,
  isLocalUpdateRef = null
}) => {
  const [versions, setVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastConversationIdRef = useRef(null);
  // Ref to coordinate version index updates without setTimeout race conditions
  const pendingVersionIndexRef = useRef(null);

  // Track the last known message count to detect new AI-generated content
  const lastMessageCountRef = useRef(0);

  // Track content hashes to detect regeneration (content change NOT from local edit)
  const contentHashesRef = useRef(new Map());

  // Track if we're in a regeneration cycle (message count decreased then increased)
  const regenerationStateRef = useRef({
    wasDecreased: false,
    previousCount: 0
  });

  useEffect(() => {
    if (!conversation?.messages) return;

    const currentMessageCount = conversation.messages.length;
    const previousCount = lastMessageCountRef.current;
    const hasNewMessages = currentMessageCount > previousCount;
    const hasFewerMessages = currentMessageCount < previousCount;

    // Detect regeneration cycle: messages decreased (old removed) then increased (new added)
    if (hasFewerMessages && !isLocalUpdateRef?.current) {
      regenerationStateRef.current = {
        wasDecreased: true,
        previousCount: previousCount
      };
    }

    // Skip processing if local edit is in progress AND no new messages
    // But still update the content hashes so they're in sync after page refresh
    if (isLocalUpdateRef?.current && !hasNewMessages) {
      lastMessageCountRef.current = currentMessageCount;

      // Update content hashes for locally edited content
      // This prevents the hook from detecting local edits as "regeneration" on page refresh
      conversation.messages.forEach((msg, originalIndex) => {
        if (msg.role === 'assistant' && isRelevantMessage(msg)) {
          const contentHash = contentFingerprint(msg.content || '');
          contentHashesRef.current.set(originalIndex, contentHash);
        }
      });

      return;
    }

    // If there are new messages, reset the local update flag (new AI content arrived)
    if (hasNewMessages && isLocalUpdateRef?.current) {
      isLocalUpdateRef.current = false;
    }

    // Update the message count
    lastMessageCountRef.current = currentMessageCount;

    // Reset versions when conversation changes
    const currentConversationId = conversation._id || conversation.id;
    if (lastConversationIdRef.current !== currentConversationId) {
      setVersions([]);
      setCurrentVersionIndex(0);
      setIsInitialized(false);
      contentHashesRef.current = new Map();
      regenerationStateRef.current = { wasDecreased: false, previousCount: 0 };
      lastConversationIdRef.current = currentConversationId;
      lastMessageCountRef.current = 0;
    }

    // Skip processing if we're still streaming (preview version handles this)
    if (isStreaming) {
      return;
    }

    // Find all relevant messages with their original indices
    const relevantMessages = [];
    conversation.messages.forEach((msg, originalIndex) => {
      if (msg.role === 'assistant' && isRelevantMessage(msg)) {
        relevantMessages.push({ msg, originalIndex });
      }
    });

    // Process each message as a version
    const newVersions = [];

    // Check if this is a regeneration cycle completion
    // (messages were decreased, now back to same or higher count)
    const isRegenerationComplete = regenerationStateRef.current.wasDecreased &&
      currentMessageCount >= regenerationStateRef.current.previousCount - 1;

    // Track which specific messages are regenerated (per-message, not global)
    const regeneratedMessageIndices = new Set();

    // First pass: identify which messages have changed content (regeneration)
    relevantMessages.forEach(({ msg, originalIndex }) => {
      const contentHash = contentFingerprint(msg.content || '');
      const previousHash = contentHashesRef.current.get(originalIndex);

      // Detect regeneration by:
      // 1. Content hash changed (different content), OR
      // 2. Regeneration cycle completed (message count went down then up)
      const contentChanged = previousHash && previousHash !== contentHash && !isLocalUpdateRef?.current;
      const cycleCompleted = isRegenerationComplete && previousHash && !isLocalUpdateRef?.current;

      if (contentChanged || cycleCompleted) {
        regeneratedMessageIndices.add(originalIndex);
      }
    });

    // Track if ANY message was regenerated (for state management)
    const hasAnyRegeneration = regeneratedMessageIndices.size > 0;

    // Second pass: create versions with correct IDs
    // Only create versions for messages with properly structured content (at least 2 sections)
    relevantMessages.forEach(({ msg, originalIndex }) => {
      const parsedContent = parseContent(msg.content, msg);

      // Require at least 2 parsed sections to create a version
      // This is reasonable since the message already passed isRelevantMessage check
      // which validates the structure more thoroughly
      const hasMinimumSections = Array.isArray(parsedContent) && parsedContent.length >= 2;

      if (parsedContent && hasMinimumSections) {
        const contentHash = contentFingerprint(msg.content || '');

        // Check if THIS specific message was regenerated
        const isThisMessageRegenerated = regeneratedMessageIndices.has(originalIndex);

        // Use message index + timestamp for ID to distinguish regenerated content
        // This ensures each regeneration gets a unique ID even if content is same
        const timestamp = msg.createdAt || new Date().toISOString();
        const messageId = isThisMessageRegenerated
          ? `msg-${originalIndex}-${timestamp}`
          : `msg-${originalIndex}`;

        const version = extractVersions ? extractVersions(msg, parsedContent, newVersions.length + 1) : {
          id: messageId,
          timestamp: msg.createdAt || new Date().toISOString(),
          content: msg.content,
          parsedContent: parsedContent,
          versionNumber: newVersions.length + 1
        };

        // Store the original message index for version tracking
        version.messageIndex = originalIndex;
        version.contentHash = contentHash;
        // Override id
        version.id = messageId;
        // Ensure parsedContent is set (for tasks that use 'sections' like tech-stack)
        if (!version.parsedContent && parsedContent) {
          version.parsedContent = parsedContent;
        }
        // Also ensure sections is set if parsedContent exists (for backward compatibility)
        if (!version.sections && parsedContent) {
          version.sections = parsedContent;
        }

        newVersions.push(version);

        // Update hash AFTER processing (so next render can detect changes)
        contentHashesRef.current.set(originalIndex, contentHash);
      }
    });

    // Inject archived versions from message_archives (backend copy-on-write snapshots)
    if (conversation.message_archives && conversation.message_archives.length > 0) {
      // Group archives by message_created_at
      const archivesByTimestamp = {};
      conversation.message_archives.forEach(archive => {
        const key = archive.message_created_at;
        if (!archivesByTimestamp[key]) {
          archivesByTimestamp[key] = [];
        }
        archivesByTimestamp[key].push(archive);
      });

      Object.entries(archivesByTimestamp).forEach(([msgTimestamp, archives]) => {
        // Sort oldest-first by archived_at
        archives.sort((a, b) => new Date(a.archived_at) - new Date(b.archived_at));

        archives.forEach((archive, archIdx) => {
          const archiveMessage = { content: archive.content, role: archive.role };
          if (!isRelevantMessage(archiveMessage)) return;

          const parsedContent = parseContent(archive.content);
          const hasMinimumSections = Array.isArray(parsedContent) && parsedContent.length >= 2;
          if (!parsedContent || !hasMinimumSections) return;

          const archiveId = `archive-${msgTimestamp}-${archIdx}`;

          // Check for duplicate (already in newVersions from a previous render)
          if (newVersions.some(v => v.id === archiveId)) return;

          const archiveVersion = extractVersions
            ? extractVersions(archiveMessage, parsedContent, 0)
            : {
                id: archiveId,
                timestamp: archive.archived_at,
                content: archive.content,
                parsedContent: parsedContent,
                versionNumber: 0
              };

          archiveVersion.id = archiveId;
          archiveVersion.timestamp = archive.archived_at;
          archiveVersion.isArchived = true;
          archiveVersion.messageCreatedAt = msgTimestamp;
          archiveVersion.messageIndex = -1; // prevents CRUD operations
          archiveVersion.contentHash = contentFingerprint(archive.content || '');

          if (!archiveVersion.parsedContent && parsedContent) {
            archiveVersion.parsedContent = parsedContent;
          }
          if (!archiveVersion.sections && parsedContent) {
            archiveVersion.sections = parsedContent;
          }

          newVersions.push(archiveVersion);
        });
      });
    }

    // Reset regeneration state after processing
    if (hasAnyRegeneration) {
      regenerationStateRef.current = {
        wasDecreased: false,
        previousCount: 0
      };
    }

    // If we have new versions, update state
    if (newVersions.length > 0) {
      setVersions(prevVersions => {
        // IMPORTANT: Filter out preview versions first to prevent "Version 2" issue
        // Preview versions should never be merged with actual versions
        // Also filter out any versions with streaming-preview ID as extra safeguard
        const nonPreviewVersions = prevVersions.filter(v =>
          !v.isPreview && v.id !== 'streaming-preview'
        );

        // Create a map of existing non-preview versions by ID for comparison
        const existingVersionsById = new Map(
          nonPreviewVersions.map(v => [v.id, v])
        );

        // Also track by message index to prevent duplicates
        const existingVersionsByMessageIndex = new Map(
          nonPreviewVersions.map(v => [v.messageIndex, v])
        );

        // Handle regeneration: keep all previous versions and add new regenerated version
        if (hasAnyRegeneration) {

          // Filter out versions that have the same ID or messageIndex (shouldn't happen, but be safe)
          // For regeneration, we only want to add versions for messages that were actually regenerated
          const uniqueNewVersions = newVersions.filter(v =>
            !existingVersionsById.has(v.id) &&
            // For regenerated messages, allow new version even if messageIndex exists
            // For non-regenerated messages, filter out if messageIndex exists
            (regeneratedMessageIndices.has(v.messageIndex) || !existingVersionsByMessageIndex.has(v.messageIndex))
          );

          const mergedVersions = [...nonPreviewVersions, ...uniqueNewVersions].sort((a, b) =>
            new Date(a.timestamp) - new Date(b.timestamp)
          );

          // Re-number versions
          mergedVersions.forEach((version, index) => {
            version.versionNumber = index + 1;
          });

          setCurrentVersionIndex(mergedVersions.length - 1);
          setIsInitialized(true);

          return mergedVersions;
        }

        // Handle local edit: update existing version content
        if (isLocalUpdateRef?.current) {
          const updatedVersions = nonPreviewVersions.map(existingVersion => {
            const newVersion = newVersions.find(v => v.messageIndex === existingVersion.messageIndex);
            if (newVersion && newVersion.content !== existingVersion.content) {
              return {
                ...existingVersion,
                content: newVersion.content,
                parsedContent: newVersion.parsedContent,
                contentHash: newVersion.contentHash,
                ...(newVersion.integrations && { integrations: newVersion.integrations }),
                ...(newVersion.features && { features: newVersion.features }),
                ...(newVersion.sections && { sections: newVersion.sections })
              };
            }
            return existingVersion;
          });
          return updatedVersions;
        }

        // Normal case: add new versions (by ID and by messageIndex to prevent duplicates)
        const uniqueNewVersions = newVersions.filter(v =>
          !existingVersionsById.has(v.id) &&
          !existingVersionsByMessageIndex.has(v.messageIndex)
        );

        if (uniqueNewVersions.length === 0 && nonPreviewVersions.length > 0) {
          if (!isInitialized && nonPreviewVersions.length > 0) {
            setCurrentVersionIndex(nonPreviewVersions.length - 1);
            setIsInitialized(true);
          }
          return nonPreviewVersions;
        }

        // Combine existing non-preview versions with unique new versions
        const updatedVersions = [...nonPreviewVersions, ...uniqueNewVersions].sort((a, b) =>
          new Date(a.timestamp) - new Date(b.timestamp)
        );

        // Re-number versions after sorting
        updatedVersions.forEach((version, index) => {
          version.versionNumber = index + 1;
        });

        // If this is first initialization or we have new versions, set to latest
        if (!isInitialized || uniqueNewVersions.length > 0) {
          setCurrentVersionIndex(updatedVersions.length - 1);
          setIsInitialized(true);
        }

        return updatedVersions;
      });
    } else if (!isInitialized && versions.length > 0) {
      // Handle case where we have existing versions but no new ones on page load
      setCurrentVersionIndex(versions.length - 1);
      setIsInitialized(true);
    }
  }, [conversation?.messages, conversation?._id, conversation?.id, isInitialized, extractVersions, parseContent, isRelevantMessage]);

  // Ensure we're always on the latest version after versions are loaded
  useEffect(() => {
    if (!isInitialized && versions.length > 0) {
      setCurrentVersionIndex(versions.length - 1);
      setIsInitialized(true);
    }
  }, [versions.length, isInitialized]);

  // Handle streaming content as a preview version
  // Only create preview if content passes strict relevance check (full project info document)
  useEffect(() => {
    if (!isStreaming || !streamingContent) return;

    // Check if streaming content is relevant
    // With stricter hasValidProjectInfoStructure, conversational responses won't pass
    const streamingMessage = { content: streamingContent, role: 'assistant' };
    const isRelevant = isRelevantMessage(streamingMessage);

    if (isRelevant) {
      const parsedContent = parseContent(streamingContent, streamingMessage);

      // Additional check: parsed content must have at least 2 sections to create a preview
      // This prevents partially streamed content from creating versions too early
      const hasEnoughSections = Array.isArray(parsedContent) && parsedContent.length >= 2;

      if (parsedContent && hasEnoughSections) {
        setVersions(prevVersions => {
          // Remove any existing preview version
          const nonPreviewVersions = prevVersions.filter(v => !v.isPreview);

          // Add new preview version with explicit streaming-preview ID
          const previewVersion = extractVersions ?
            extractVersions(streamingMessage, parsedContent, nonPreviewVersions.length + 1, true) : {
              id: 'streaming-preview',
              timestamp: new Date().toISOString(),
              content: streamingContent,
              parsedContent: parsedContent,
              versionNumber: nonPreviewVersions.length + 1,
              isPreview: true,
              label: 'Generating...'
            };

          // Ensure preview version is marked correctly with explicit ID
          previewVersion.isPreview = true;
          previewVersion.id = 'streaming-preview'; // Always use this ID for streaming preview

          // Ensure both sections and parsedContent are set for consistency
          if (!previewVersion.sections && parsedContent) {
            previewVersion.sections = parsedContent;
          }
          if (!previewVersion.parsedContent && parsedContent) {
            previewVersion.parsedContent = parsedContent;
          }

          // Use callback to get correct index
          const newVersions = [...nonPreviewVersions, previewVersion];

          // Schedule index update via ref — applied by useEffect after versions state commits
          pendingVersionIndexRef.current = newVersions.length - 1;

          return newVersions;
        });
      }
    }
    // If content is NOT relevant (conversational response), do nothing
    // The RHS will stay on the current version
  }, [isStreaming, streamingContent, extractVersions, parseContent, isRelevantMessage]);

  // Clean up preview version when streaming completes
  // Also ensure version index points to a valid version
  useEffect(() => {
    if (!isStreaming) {
      setVersions(prevVersions => {
        const hadPreview = prevVersions.some(v => v.isPreview);
        const filtered = prevVersions.filter(v => !v.isPreview);

        // Renumber versions after removing previews to ensure correct version numbers
        filtered.forEach((version, index) => {
          version.versionNumber = index + 1;
        });

        // If we had a preview and removed it, reset index to last actual version
        // This prevents the index from pointing to a non-existent preview
        if (hadPreview && filtered.length > 0) {
          // Schedule index update via ref — applied by useEffect after versions state commits
          pendingVersionIndexRef.current = filtered.length - 1;
        }

        return filtered;
      });
    }
  }, [isStreaming]);

  // Apply any pending version index changes after versions state commits.
  // This replaces setTimeout-based index updates which caused race conditions.
  useEffect(() => {
    if (pendingVersionIndexRef.current !== null) {
      setCurrentVersionIndex(pendingVersionIndexRef.current);
      pendingVersionIndexRef.current = null;
    }
  }, [versions]);

  const currentVersion = versions[currentVersionIndex] || null;

  // Helper function to add a new version manually
  const addVersion = (content, parsedContent, metadata = {}) => {
    const newVersion = {
      id: `manual-${Date.now()}`,
      timestamp: new Date().toISOString(),
      content: content,
      parsedContent: parsedContent,
      isManual: true,
      ...metadata
    };

    setVersions(prev => {
      newVersion.versionNumber = prev.length + 1;
      const updated = [...prev, newVersion];
      pendingVersionIndexRef.current = updated.length - 1;
      return updated;
    });

    return newVersion;
  };

  // Helper function to update current version
  const updateCurrentVersion = (updates) => {
    if (!currentVersion || currentVersion.isPreview) return;

    setVersions(prev => prev.map(v =>
      v.id === currentVersion.id ? { ...v, ...updates } : v
    ));
  };

  return {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions: versions.length > 0,
    isLatestVersion: currentVersionIndex === versions.length - 1,
    addVersion,
    updateCurrentVersion
  };
};

export default useVersionManagementV1;
